import type { ContentBlock } from '@/types/cms';
import { generateBlockId } from '@/types/cms';
import { FileText, Shield, Info, HelpCircle, PenTool, Phone, File } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ════════════════════════════════════════════════════════════
// CMS Page Templates
// Pre-built templates with EN/FR content blocks
// ════════════════════════════════════════════════════════════

export interface CmsTemplate {
  id: string;
  name_en: string;
  name_fr: string;
  description_en: string;
  description_fr: string;
  icon: LucideIcon;
  color: string;
  title_en: string;
  title_fr: string;
  subtitle_en: string;
  subtitle_fr: string;
  content_en: ContentBlock[];
  content_fr: ContentBlock[];
}

function b(type: ContentBlock['type'], props: Omit<any, 'type' | 'id'>): ContentBlock {
  return { type, id: generateBlockId(), ...props } as ContentBlock;
}

export const CMS_TEMPLATES: CmsTemplate[] = [
  // ── Blank ──
  {
    id: 'blank',
    name_en: 'Blank Page',
    name_fr: 'Page vierge',
    description_en: 'Start from scratch with an empty page',
    description_fr: 'Commencez de zéro avec une page vide',
    icon: File,
    color: '#6b7280',
    title_en: 'New Page',
    title_fr: 'Nouvelle Page',
    subtitle_en: '',
    subtitle_fr: '',
    content_en: [b('paragraph', { text: 'Start writing your content here...' })],
    content_fr: [b('paragraph', { text: 'Commencez à rédiger votre contenu ici...' })],
  },

  // ── Terms of Service ──
  {
    id: 'terms',
    name_en: 'Terms of Service',
    name_fr: 'Conditions Générales',
    description_en: 'Standard terms of service template',
    description_fr: 'Modèle standard de conditions générales',
    icon: FileText,
    color: '#3b82f6',
    title_en: 'Terms of Service',
    title_fr: "Conditions Générales d'Utilisation",
    subtitle_en: 'The rules that govern your use of our platform',
    subtitle_fr: 'Les règles qui régissent votre utilisation de notre plateforme',
    content_en: [
      b('heading', { level: 2, text: '1. Purpose' }),
      b('paragraph', {
        text: 'These Terms of Service ("Terms") define the conditions under which you may access and use our platform. By creating an account, you agree to these terms without reservation.',
      }),
      b('heading', { level: 2, text: '2. Description of the Service' }),
      b('paragraph', {
        text: 'Our platform provides tools and services to help you manage your personal finances, including:',
      }),
      b('list', {
        style: 'bullet',
        items: [
          'Tracking bank accounts and assets',
          'Recording and categorizing transactions',
          'Creating and monitoring budgets',
          'Setting and tracking savings goals',
          'Visualizing financial data through dashboards',
        ],
      }),
      b('callout', {
        variant: 'warning',
        text: 'This platform is a tracking and organization tool. It does not constitute financial, tax, or investment advice.',
      }),
      b('heading', { level: 2, text: '3. Account Registration' }),
      b('heading', { level: 3, text: '3.1 Registration Requirements' }),
      b('paragraph', {
        text: 'Access to the platform requires creating an account with a valid email address and a secure password. You agree to provide accurate and up-to-date information.',
      }),
      b('heading', { level: 3, text: '3.2 Account Security' }),
      b('paragraph', {
        text: 'You are responsible for the confidentiality of your login credentials. You must immediately report any unauthorized use of your account. Two-factor authentication (2FA) is available to enhance account security.',
      }),
      b('heading', { level: 2, text: '4. Acceptable Use' }),
      b('paragraph', {
        text: 'You agree to use the platform in compliance with applicable laws and these terms. The following are prohibited:',
      }),
      b('list', {
        style: 'bullet',
        items: [
          'Using the platform for illegal or fraudulent purposes',
          "Attempting to access other users' data",
          'Circumventing security measures',
          'Using automated systems (bots, scrapers) to access the platform',
          'Disrupting platform operations',
        ],
      }),
      b('heading', { level: 2, text: '5. Personal Data' }),
      b('paragraph', {
        text: 'The processing of personal data is governed by our Privacy Policy, which forms an integral part of these terms.',
      }),
      b('heading', { level: 2, text: '6. Intellectual Property' }),
      b('paragraph', {
        text: 'All elements constituting the platform (design, code, texts, logos, trademarks) are protected by intellectual property law and are the exclusive property of the platform.',
      }),
      b('heading', { level: 2, text: '7. Limitation of Liability' }),
      b('paragraph', {
        text: 'The platform is provided "as is" without warranty. We shall not be liable for any indirect damages, data loss, or issues resulting from platform use.',
      }),
      b('heading', { level: 2, text: '8. Modifications' }),
      b('paragraph', {
        text: 'We reserve the right to modify these terms at any time. Users will be notified of significant changes. Continued use after modifications constitutes acceptance.',
      }),
      b('heading', { level: 2, text: '9. Contact' }),
      b('paragraph', {
        text: 'For any questions regarding these terms, please contact us at: contact@example.com',
      }),
    ],
    content_fr: [
      b('heading', { level: 2, text: '1. Objet' }),
      b('paragraph', {
        text: "Les présentes Conditions Générales d'Utilisation (« CGU ») ont pour objet de définir les modalités et conditions d'utilisation de notre plateforme. En créant un compte, vous acceptez sans réserve les présentes CGU.",
      }),
      b('heading', { level: 2, text: '2. Description du service' }),
      b('paragraph', {
        text: 'Notre plateforme fournit des outils de gestion financière personnelle permettant de :',
      }),
      b('list', {
        style: 'bullet',
        items: [
          'Gérer et suivre vos comptes bancaires et actifs',
          'Enregistrer et catégoriser vos transactions',
          'Créer et suivre des budgets par catégorie',
          "Définir et suivre des objectifs d'épargne",
          'Visualiser vos données financières via des tableaux de bord',
        ],
      }),
      b('callout', {
        variant: 'warning',
        text: "Cette plateforme est un outil de suivi et d'organisation. Elle ne constitue en aucun cas un conseil financier, fiscal ou d'investissement.",
      }),
      b('heading', { level: 2, text: '3. Accès et inscription' }),
      b('heading', { level: 3, text: "3.1 Conditions d'inscription" }),
      b('paragraph', {
        text: "L'accès à la plateforme nécessite la création d'un compte avec une adresse email valide et un mot de passe sécurisé. Vous vous engagez à fournir des informations exactes et à jour.",
      }),
      b('heading', { level: 3, text: '3.2 Sécurité du compte' }),
      b('paragraph', {
        text: "Vous êtes responsable de la confidentialité de vos identifiants de connexion. Vous vous engagez à informer immédiatement la plateforme de toute utilisation non autorisée. L'authentification à deux facteurs (2FA) est disponible pour renforcer la sécurité.",
      }),
      b('heading', { level: 2, text: '4. Utilisation autorisée' }),
      b('paragraph', {
        text: 'Vous vous engagez à utiliser la plateforme de manière conforme aux lois en vigueur et aux présentes CGU. Il est notamment interdit de :',
      }),
      b('list', {
        style: 'bullet',
        items: [
          'Utiliser la plateforme à des fins illicites ou frauduleuses',
          "Tenter d'accéder aux données d'autres utilisateurs",
          'Contourner les mesures de sécurité',
          'Utiliser des systèmes automatisés (robots, scrapers)',
          'Perturber le fonctionnement de la plateforme',
        ],
      }),
      b('heading', { level: 2, text: '5. Données personnelles' }),
      b('paragraph', {
        text: 'Le traitement des données personnelles est régi par notre Politique de Confidentialité, qui fait partie intégrante des présentes CGU.',
      }),
      b('heading', { level: 2, text: '6. Propriété intellectuelle' }),
      b('paragraph', {
        text: "L'ensemble des éléments constituant la plateforme (design, code, textes, logos, marques) sont protégés par le droit de la propriété intellectuelle et sont la propriété exclusive de la plateforme.",
      }),
      b('heading', { level: 2, text: '7. Responsabilité' }),
      b('paragraph', {
        text: "La plateforme est fournie « en l'état » sans garantie. Nous ne serons pas responsables des dommages indirects, perte de données ou tout problème résultant de l'utilisation de la plateforme.",
      }),
      b('heading', { level: 2, text: '8. Modifications' }),
      b('paragraph', {
        text: "Nous nous réservons le droit de modifier ces conditions à tout moment. Les utilisateurs seront informés des modifications importantes. L'utilisation continue après modification vaut acceptation.",
      }),
      b('heading', { level: 2, text: '9. Contact' }),
      b('paragraph', {
        text: 'Pour toute question relative aux présentes CGU, veuillez nous contacter à : contact@example.com',
      }),
    ],
  },

  // ── Privacy Policy ──
  {
    id: 'privacy',
    name_en: 'Privacy Policy',
    name_fr: 'Politique de Confidentialité',
    description_en: 'GDPR-compliant privacy policy template',
    description_fr: 'Modèle de politique de confidentialité conforme RGPD',
    icon: Shield,
    color: '#8b5cf6',
    title_en: 'Privacy Policy',
    title_fr: 'Politique de Confidentialité',
    subtitle_en: 'How we collect, use, and protect your data',
    subtitle_fr: 'Comment nous collectons, utilisons et protégeons vos données',
    content_en: [
      b('callout', {
        variant: 'info',
        text: 'This policy complies with the General Data Protection Regulation (GDPR) and explains how we handle your personal data.',
      }),
      b('heading', { level: 2, text: '1. Data We Collect' }),
      b('paragraph', { text: 'We collect and process the following categories of personal data:' }),
      b('list', {
        style: 'bullet',
        items: [
          'Identity data: name, email address, date of birth',
          'Financial data: account information, transactions, budgets, goals (entered by you)',
          'Technical data: IP address, browser type, device information',
          'Usage data: features used, pages visited, session duration',
        ],
      }),
      b('heading', { level: 2, text: '2. How We Use Your Data' }),
      b('list', {
        style: 'bullet',
        items: [
          'Providing and maintaining the service',
          'Personalizing your experience',
          'Sending important service notifications',
          'Improving our platform through analytics',
          'Ensuring security and preventing fraud',
        ],
      }),
      b('heading', { level: 2, text: '3. Data Storage & Security' }),
      b('paragraph', {
        text: 'Your data is stored securely using industry-standard encryption. We use Supabase (PostgreSQL) with Row Level Security (RLS) ensuring each user can only access their own data.',
      }),
      b('callout', {
        variant: 'success',
        text: 'All data is encrypted in transit (TLS) and at rest. We never store plain-text passwords.',
      }),
      b('heading', { level: 2, text: '4. Your Rights (GDPR)' }),
      b('paragraph', { text: 'Under GDPR, you have the following rights:' }),
      b('list', {
        style: 'numbered',
        items: [
          'Right of access — request a copy of your data',
          'Right to rectification — correct inaccurate data',
          'Right to erasure — request deletion of your data',
          'Right to portability — receive your data in a structured format',
          'Right to object — object to certain processing activities',
          'Right to restrict processing — limit how we use your data',
        ],
      }),
      b('heading', { level: 2, text: '5. Cookies' }),
      b('paragraph', {
        text: 'We use essential cookies for authentication and session management. Analytics cookies are only used with your consent.',
      }),
      b('heading', { level: 2, text: '6. Data Retention' }),
      b('paragraph', {
        text: 'We retain your data for as long as your account is active. Upon account deletion, all personal data is permanently removed within 30 days.',
      }),
      b('heading', { level: 2, text: '7. Contact' }),
      b('paragraph', {
        text: 'For any questions about your data privacy, contact our Data Protection Officer at: privacy@example.com',
      }),
    ],
    content_fr: [
      b('callout', {
        variant: 'info',
        text: 'Cette politique est conforme au Règlement Général sur la Protection des Données (RGPD) et explique comment nous traitons vos données personnelles.',
      }),
      b('heading', { level: 2, text: '1. Données collectées' }),
      b('paragraph', {
        text: 'Nous collectons et traitons les catégories suivantes de données personnelles :',
      }),
      b('list', {
        style: 'bullet',
        items: [
          "Données d'identité : nom, adresse email, date de naissance",
          'Données financières : informations de comptes, transactions, budgets, objectifs (saisies par vous)',
          'Données techniques : adresse IP, type de navigateur, appareil',
          "Données d'utilisation : fonctionnalités utilisées, pages visitées, durée de session",
        ],
      }),
      b('heading', { level: 2, text: '2. Utilisation de vos données' }),
      b('list', {
        style: 'bullet',
        items: [
          'Fournir et maintenir le service',
          'Personnaliser votre expérience',
          'Envoyer des notifications de service importantes',
          'Améliorer notre plateforme via des analyses',
          'Assurer la sécurité et prévenir la fraude',
        ],
      }),
      b('heading', { level: 2, text: '3. Stockage et sécurité' }),
      b('paragraph', {
        text: "Vos données sont stockées de manière sécurisée avec un chiffrement aux standards de l'industrie. Nous utilisons Supabase (PostgreSQL) avec Row Level Security (RLS) garantissant que chaque utilisateur ne peut accéder qu'à ses propres données.",
      }),
      b('callout', {
        variant: 'success',
        text: 'Toutes les données sont chiffrées en transit (TLS) et au repos. Nous ne stockons jamais de mots de passe en clair.',
      }),
      b('heading', { level: 2, text: '4. Vos droits (RGPD)' }),
      b('paragraph', { text: 'En vertu du RGPD, vous disposez des droits suivants :' }),
      b('list', {
        style: 'numbered',
        items: [
          "Droit d'accès — demander une copie de vos données",
          'Droit de rectification — corriger des données inexactes',
          "Droit à l'effacement — demander la suppression de vos données",
          'Droit à la portabilité — recevoir vos données dans un format structuré',
          "Droit d'opposition — vous opposer à certains traitements",
          'Droit à la limitation — limiter comment nous utilisons vos données',
        ],
      }),
      b('heading', { level: 2, text: '5. Cookies' }),
      b('paragraph', {
        text: "Nous utilisons des cookies essentiels pour l'authentification et la gestion de session. Les cookies analytiques ne sont utilisés qu'avec votre consentement.",
      }),
      b('heading', { level: 2, text: '6. Conservation des données' }),
      b('paragraph', {
        text: 'Nous conservons vos données tant que votre compte est actif. Lors de la suppression du compte, toutes les données personnelles sont définitivement supprimées sous 30 jours.',
      }),
      b('heading', { level: 2, text: '7. Contact' }),
      b('paragraph', {
        text: 'Pour toute question relative à la confidentialité de vos données, contactez notre Délégué à la Protection des Données à : privacy@example.com',
      }),
    ],
  },

  // ── About Page ──
  {
    id: 'about',
    name_en: 'About',
    name_fr: 'À propos',
    description_en: 'Company information and mission page',
    description_fr: "Page d'information sur l'entreprise et sa mission",
    icon: Info,
    color: '#16c47f',
    title_en: 'About Us',
    title_fr: 'À propos de nous',
    subtitle_en: 'Our mission, values, and the team behind the platform',
    subtitle_fr: "Notre mission, nos valeurs et l'équipe derrière la plateforme",
    content_en: [
      b('heading', { level: 2, text: 'Our Mission' }),
      b('paragraph', {
        text: 'We believe that everyone deserves clear, simple, and powerful tools to manage their financial life. Our mission is to democratize personal finance management by providing an intuitive platform that helps you take control of your money.',
      }),
      b('callout', {
        variant: 'success',
        text: 'Empowering you to make better financial decisions, one transaction at a time.',
      }),
      b('heading', { level: 2, text: 'Our Values' }),
      b('list', {
        style: 'bullet',
        items: [
          '**Simplicity** — We make complex financial data easy to understand',
          '**Privacy** — Your financial data belongs to you, always',
          '**Transparency** — No hidden fees, no data selling, no surprises',
          '**Accessibility** — Built for everyone, regardless of financial expertise',
        ],
      }),
      b('heading', { level: 2, text: 'What We Offer' }),
      b('paragraph', {
        text: 'Our platform provides a comprehensive suite of tools for personal finance management:',
      }),
      b('list', {
        style: 'numbered',
        items: [
          'Multi-account tracking across banks, crypto, and investments',
          'Smart transaction categorization and insights',
          'Budget planning and goal setting',
          'Beautiful dashboards and analytics',
          'AI-powered financial assistant',
        ],
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Contact Us' }),
      b('paragraph', {
        text: "Have questions? We'd love to hear from you. Reach out at contact@example.com",
      }),
    ],
    content_fr: [
      b('heading', { level: 2, text: 'Notre Mission' }),
      b('paragraph', {
        text: 'Nous croyons que chacun mérite des outils clairs, simples et puissants pour gérer sa vie financière. Notre mission est de démocratiser la gestion financière personnelle en fournissant une plateforme intuitive qui vous aide à prendre le contrôle de votre argent.',
      }),
      b('callout', {
        variant: 'success',
        text: 'Vous aider à prendre de meilleures décisions financières, une transaction à la fois.',
      }),
      b('heading', { level: 2, text: 'Nos Valeurs' }),
      b('list', {
        style: 'bullet',
        items: [
          '**Simplicité** — Nous rendons les données financières complexes faciles à comprendre',
          '**Confidentialité** — Vos données financières vous appartiennent, toujours',
          '**Transparence** — Pas de frais cachés, pas de vente de données, pas de surprises',
          "**Accessibilité** — Conçu pour tous, quel que soit le niveau d'expertise financière",
        ],
      }),
      b('heading', { level: 2, text: 'Ce que nous offrons' }),
      b('paragraph', {
        text: "Notre plateforme fournit une suite complète d'outils de gestion financière :",
      }),
      b('list', {
        style: 'numbered',
        items: [
          'Suivi multi-comptes : banques, crypto et investissements',
          'Catégorisation intelligente des transactions',
          'Planification de budget et objectifs',
          'Tableaux de bord et analyses visuelles',
          "Assistant financier propulsé par l'IA",
        ],
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Nous Contacter' }),
      b('paragraph', {
        text: 'Des questions ? Nous serions ravis de vous entendre. Contactez-nous à contact@example.com',
      }),
    ],
  },

  // ── FAQ ──
  {
    id: 'faq',
    name_en: 'FAQ',
    name_fr: 'FAQ',
    description_en: 'Frequently asked questions page',
    description_fr: 'Page des questions fréquemment posées',
    icon: HelpCircle,
    color: '#f59e0b',
    title_en: 'Frequently Asked Questions',
    title_fr: 'Questions Fréquentes',
    subtitle_en: 'Find answers to the most common questions',
    subtitle_fr: 'Trouvez les réponses aux questions les plus courantes',
    content_en: [
      b('heading', { level: 2, text: 'Getting Started' }),
      b('heading', { level: 3, text: 'How do I create an account?' }),
      b('paragraph', {
        text: 'Click "Sign Up" on the homepage, enter your email and a strong password, then complete the onboarding process to personalize your experience.',
      }),
      b('heading', { level: 3, text: 'Is it free to use?' }),
      b('paragraph', {
        text: 'Yes! The platform is completely free to use with all features included. We believe financial tools should be accessible to everyone.',
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Features' }),
      b('heading', { level: 3, text: 'Can I track multiple bank accounts?' }),
      b('paragraph', {
        text: 'Absolutely! You can add unlimited accounts including bank accounts, crypto wallets, investment portfolios, real estate, and more.',
      }),
      b('heading', { level: 3, text: 'How does the AI assistant work?' }),
      b('paragraph', {
        text: 'Our AI assistant analyzes your financial data to provide personalized insights, spending patterns, and savings recommendations. All analysis happens securely with your data.',
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Security & Privacy' }),
      b('heading', { level: 3, text: 'Is my financial data secure?' }),
      b('paragraph', {
        text: 'Yes. We use bank-level encryption, Row Level Security in our database, and offer two-factor authentication (2FA). Your data is never shared or sold.',
      }),
      b('callout', {
        variant: 'info',
        text: "Have a question that isn't answered here? Contact us at support@example.com",
      }),
    ],
    content_fr: [
      b('heading', { level: 2, text: 'Premiers pas' }),
      b('heading', { level: 3, text: 'Comment créer un compte ?' }),
      b('paragraph', {
        text: "Cliquez sur « S'inscrire » sur la page d'accueil, entrez votre email et un mot de passe sécurisé, puis complétez le processus d'onboarding pour personnaliser votre expérience.",
      }),
      b('heading', { level: 3, text: 'Est-ce gratuit ?' }),
      b('paragraph', {
        text: 'Oui ! La plateforme est entièrement gratuite avec toutes les fonctionnalités incluses. Nous croyons que les outils financiers doivent être accessibles à tous.',
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Fonctionnalités' }),
      b('heading', { level: 3, text: 'Puis-je suivre plusieurs comptes bancaires ?' }),
      b('paragraph', {
        text: 'Absolument ! Vous pouvez ajouter un nombre illimité de comptes : comptes bancaires, portefeuilles crypto, investissements, immobilier, et plus encore.',
      }),
      b('heading', { level: 3, text: "Comment fonctionne l'assistant IA ?" }),
      b('paragraph', {
        text: "Notre assistant IA analyse vos données financières pour fournir des insights personnalisés, des tendances de dépenses et des recommandations d'épargne. Toute l'analyse se fait de manière sécurisée avec vos données.",
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Sécurité & Confidentialité' }),
      b('heading', { level: 3, text: 'Mes données financières sont-elles sécurisées ?' }),
      b('paragraph', {
        text: "Oui. Nous utilisons un chiffrement de niveau bancaire, Row Level Security dans notre base de données, et proposons l'authentification à deux facteurs (2FA). Vos données ne sont jamais partagées ni vendues.",
      }),
      b('callout', {
        variant: 'info',
        text: 'Vous avez une question sans réponse ici ? Contactez-nous à support@example.com',
      }),
    ],
  },

  // ── Blog Post ──
  {
    id: 'blog',
    name_en: 'Blog Post',
    name_fr: 'Article de Blog',
    description_en: 'Article or announcement template',
    description_fr: "Modèle d'article ou d'annonce",
    icon: PenTool,
    color: '#ec4899',
    title_en: 'Blog Post Title',
    title_fr: "Titre de l'Article",
    subtitle_en: 'A brief description of this article',
    subtitle_fr: 'Une brève description de cet article',
    content_en: [
      b('paragraph', {
        text: 'Write your introduction here. Set the context and hook the reader with a compelling opening paragraph.',
      }),
      b('heading', { level: 2, text: 'The Main Topic' }),
      b('paragraph', {
        text: 'Develop your main ideas in this section. Use clear, concise language and break up long passages into digestible paragraphs.',
      }),
      b('quote', {
        text: 'Add an inspiring quote or key takeaway to make your article memorable.',
        author: '',
      }),
      b('heading', { level: 2, text: 'Key Points' }),
      b('list', {
        style: 'numbered',
        items: [
          'First key point or takeaway',
          'Second important insight',
          'Third conclusion or action item',
        ],
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Conclusion' }),
      b('paragraph', {
        text: 'Wrap up your article with a strong conclusion. Summarize the key points and include a call to action.',
      }),
    ],
    content_fr: [
      b('paragraph', {
        text: "Rédigez votre introduction ici. Définissez le contexte et accrochez le lecteur avec un paragraphe d'ouverture captivant.",
      }),
      b('heading', { level: 2, text: 'Le Sujet Principal' }),
      b('paragraph', {
        text: 'Développez vos idées principales dans cette section. Utilisez un langage clair et concis et découpez les passages longs en paragraphes digestes.',
      }),
      b('quote', {
        text: 'Ajoutez une citation inspirante ou un point clé pour rendre votre article mémorable.',
        author: '',
      }),
      b('heading', { level: 2, text: 'Points Clés' }),
      b('list', {
        style: 'numbered',
        items: [
          'Premier point clé ou enseignement',
          'Deuxième insight important',
          'Troisième conclusion ou action à entreprendre',
        ],
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Conclusion' }),
      b('paragraph', {
        text: "Concluez votre article avec une conclusion forte. Résumez les points clés et incluez un appel à l'action.",
      }),
    ],
  },

  // ── Contact ──
  {
    id: 'contact',
    name_en: 'Contact',
    name_fr: 'Contact',
    description_en: 'Contact information page',
    description_fr: 'Page de coordonnées',
    icon: Phone,
    color: '#06b6d4',
    title_en: 'Contact Us',
    title_fr: 'Nous Contacter',
    subtitle_en: "We'd love to hear from you",
    subtitle_fr: 'Nous serions ravis de vous entendre',
    content_en: [
      b('paragraph', {
        text: "Whether you have a question, feedback, or need help with your account, don't hesitate to reach out. We're here to help!",
      }),
      b('heading', { level: 2, text: 'Email' }),
      b('paragraph', { text: 'For general inquiries: **contact@example.com**' }),
      b('paragraph', { text: 'For technical support: **support@example.com**' }),
      b('paragraph', { text: 'For privacy concerns: **privacy@example.com**' }),
      b('heading', { level: 2, text: 'Response Time' }),
      b('callout', {
        variant: 'info',
        text: 'We aim to respond to all inquiries within 24-48 business hours.',
      }),
      b('heading', { level: 2, text: 'Social Media' }),
      b('paragraph', {
        text: 'Follow us on social media for the latest updates, tips, and announcements.',
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Bug Reports' }),
      b('paragraph', {
        text: 'Found a bug? Please describe the issue in detail, including the steps to reproduce it, your browser, and device. This helps us fix it faster!',
      }),
    ],
    content_fr: [
      b('paragraph', {
        text: "Que vous ayez une question, un retour ou besoin d'aide avec votre compte, n'hésitez pas à nous contacter. Nous sommes là pour vous aider !",
      }),
      b('heading', { level: 2, text: 'Email' }),
      b('paragraph', { text: 'Pour les demandes générales : **contact@example.com**' }),
      b('paragraph', { text: 'Pour le support technique : **support@example.com**' }),
      b('paragraph', { text: 'Pour la confidentialité : **privacy@example.com**' }),
      b('heading', { level: 2, text: 'Temps de réponse' }),
      b('callout', {
        variant: 'info',
        text: 'Nous nous efforçons de répondre à toutes les demandes sous 24-48 heures ouvrées.',
      }),
      b('heading', { level: 2, text: 'Réseaux Sociaux' }),
      b('paragraph', {
        text: 'Suivez-nous sur les réseaux sociaux pour les dernières actualités, conseils et annonces.',
      }),
      b('divider', {}),
      b('heading', { level: 2, text: 'Rapport de Bugs' }),
      b('paragraph', {
        text: 'Vous avez trouvé un bug ? Décrivez le problème en détail, incluant les étapes pour le reproduire, votre navigateur et votre appareil. Cela nous aide à le corriger plus rapidement !',
      }),
    ],
  },
];

export function getTemplate(id: string): CmsTemplate | undefined {
  return CMS_TEMPLATES.find((t) => t.id === id);
}
